/**
 * Orders Section Utilities
 */

import { getLocalizedValue } from '@spcc/core'
import type { LocalizedText } from '../../../domain/types'
import { normalizeOrderStatus } from './constants'

export function getStatusTranslationKey(status: string | null | undefined): string {
  const normalized = normalizeOrderStatus(status ?? undefined)
  if (normalized) return normalized

  const fallback = status?.trim().toLowerCase()
  return fallback && fallback.length > 0 ? fallback : 'unknown'
}

export function formatCurrency(amount: number, currency: string): string {
  return `${currency} ${amount.toLocaleString()}`
}

export function stopPropagation(e: React.MouseEvent): void {
  e.preventDefault(); e.stopPropagation()
}

export function formatDateOnly(value: string | undefined): string {
  if (!value) return '—'
  const dateStr = value.split('T')[0]
  return dateStr || '—'
}

export function formatTimeOnly(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

export function formatDurationFromSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return '—'

  const totalMinutes = Math.floor(seconds / 60)
  if (totalMinutes === 0) return `${seconds}s`
  if (totalMinutes < 60) return `${totalMinutes}m`

  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60

  if (days > 0) return `${days}d ${hours}h`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

export function formatProcessingToDeliveredDuration(
  seconds: number | null | undefined
): string {
  return formatDurationFromSeconds(seconds)
}

export function formatLoadingDuration(
  seconds: number | null | undefined
): string {
  return formatDurationFromSeconds(seconds)
}

export function getLocalizedQuantityUnit(
  unit: string | undefined,
  t: (key: string) => string,
  language?: string,
): string {
  if (!unit) return ''

  const normalizedUnit = unit.trim().toLowerCase()
  const normalizedLanguage = language?.toLowerCase() ?? ''

  if (normalizedUnit === 'bags' || normalizedUnit === 'bag') {
    if (normalizedLanguage.startsWith('ar')) return 'أكياس'
    if (normalizedLanguage.startsWith('ur')) return 'تھیلے'
    return t('orders.bags')
  }

  if (normalizedUnit === 'tons' || normalizedUnit === 'ton') {
    if (normalizedLanguage.startsWith('ar')) return 'طن'
    if (normalizedLanguage.startsWith('ur')) return 'ٹن'
    return t('orders.tons')
  }

  if (normalizedUnit === 'kg') {
    if (normalizedLanguage.startsWith('ar')) return 'كجم'
    if (normalizedLanguage.startsWith('ur')) return 'کلوگرام'
    return t('orders.kg')
  }

  if (normalizedUnit === 'bulk') {
    if (normalizedLanguage.startsWith('ar')) return 'سائب'
    if (normalizedLanguage.startsWith('ur')) return 'بلک'
    return t('orders.bulk')
  }

  return unit
}

const CITY_LABELS: Record<string, { ar: string; ur: string }> = {
  riyadh: { ar: 'الرياض', ur: 'ریاض' },
  jeddah: { ar: 'جدة', ur: 'جدہ' },
  makkah: { ar: 'مكة', ur: 'مکہ' },
  mecca: { ar: 'مكة', ur: 'مکہ' },
  madinah: { ar: 'المدينة المنورة', ur: 'مدینہ منورہ' },
  medina: { ar: 'المدينة المنورة', ur: 'مدینہ منورہ' },
  dammam: { ar: 'الدمام', ur: 'دمام' },
  khobar: { ar: 'الخبر', ur: 'خبر' },
  alkhobar: { ar: 'الخبر', ur: 'الخبر' },
  jubail: { ar: 'الجبيل', ur: 'جبیل' },
  yanbu: { ar: 'ينبع', ur: 'ینبع' },
  taif: { ar: 'الطائف', ur: 'طائف' },
  tabuk: { ar: 'تبوك', ur: 'تبوک' },
  hail: { ar: 'حائل', ur: 'حائل' },
  abha: { ar: 'أبها', ur: 'ابہا' },
  jazan: { ar: 'جازان', ur: 'جازان' },
  najran: { ar: 'نجران', ur: 'نجران' },
  buraidah: { ar: 'بريدة', ur: 'بریدہ' },
  buraydah: { ar: 'بريدة', ur: 'بریدہ' },
  sakaka: { ar: 'سكاكا', ur: 'سکاکا' },
  arar: { ar: 'عرعر', ur: 'عرعر' },
  albahah: { ar: 'الباحة', ur: 'الباحہ' },
  'al-bahah': { ar: 'الباحة', ur: 'الباحہ' },
}

export function getLocalizedCityName(
  city: string | undefined,
  language: string,
): string | undefined {
  if (!city) return undefined

  const trimmedCity = city.trim()
  if (!trimmedCity) return undefined

  if (language.startsWith('ar')) {
    return CITY_LABELS[trimmedCity.toLowerCase()]?.ar ?? trimmedCity
  }

  if (language.startsWith('ur')) {
    return CITY_LABELS[trimmedCity.toLowerCase()]?.ur ?? trimmedCity
  }

  return trimmedCity
}

export function getLocalizedProductName(
  product: LocalizedText | undefined,
  language: string,
): string | undefined {
  const localizedName = getLocalizedValue(product, language).trim()
  return localizedName || undefined
}

export function getLocalizedDeliveryLocation(
  deliveryAddress: string | undefined,
  dropoffCity: string | undefined,
  language: string,
): string | undefined {
  const localizedAddress = getLocalizedCityName(deliveryAddress, language)
  if (localizedAddress) return localizedAddress

  return getLocalizedCityName(dropoffCity, language)
}

/**
 * Convert kg to tons, rounded to 3 decimal places.
 * Returns null when kg is absent or non-positive.
 */
export function kgToTons(kg: number | null | undefined): number | null {
  if (kg == null || kg <= 0) return null
  return Math.round((kg / 1000) * 1000) / 1000
}

/**
 * Convert bags to tons using the per-bag weight in kg.
 * Returns null when bagWeightKg is absent or non-positive.
 */
export function bagsToTons(bags: number, bagWeightKg: number | null | undefined): number | null {
  if (bagWeightKg == null || bagWeightKg <= 0) return null
  return kgToTons(bags * bagWeightKg)
}
